import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import styled, { ThemeProvider, useTheme } from "styled-components";
import { default as ReactSelect, components } from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { space, layout, compose } from "styled-system";
import { css } from "@styled-system/css";
import shouldForwardProp from "@styled-system/should-forward-prop";
import { themeGet } from "@styled-system/theme-get";
import { Small } from "../Typography";
import useInputFocus from "../../hooks/useInputFocus";

const SelectStyles = compose(space, layout);

const Wrapper = styled("div")
  .withConfig({ shouldForwardProp })
  .attrs((props) => ({
    "data-testid": props["data-testid"] ? props["data-testid"] : null,
  }))(
  css({
    display: "inline-block",
    width: "100%",
  }),
  SelectStyles
);

const Label = styled("label")
  .withConfig({ shouldForwardProp })
  .attrs((props) => ({
    htmlFor: props.htmlFor ?? props.id,
  }))((props) =>
  css({
    display: "block",
    color: props.invalid
      ? themeGet("colors.dangerDark")(props)
      : themeGet("colors.bodyText")(props),
    fontSize: themeGet("fontSizes.0")(props),
    fontWeight: props.bold
      ? themeGet("fontWeights.2")(props)
      : themeGet("fontWeights.1")(props),
    mb: "xs",
  })
);

const MultiValueRemove = (props) => {
  const { innerProps, data } = props;

  return (
    <components.MultiValueRemove
      {...{
        ...props,
        innerProps: { ...innerProps, "aria-label": `Remove ${data.label}` },
      }}
    />
  );
};

MultiValueRemove.propTypes = {
  data: PropTypes.object,
  innerProps: PropTypes.object,
};

const controlBorderColor = (theme, { invalid, emphasis }) => {
  if (invalid) {
    return themeGet("colors.danger")({ theme });
  }
  if (emphasis) {
    return themeGet("colors.primary")({ theme });
  }
  return themeGet("colors.surfaceRaisedBorder")({ theme });
};

const SELECT_TYPES = {
  creatable: CreatableSelect,
  createable: CreatableSelect,
  async: AsyncSelect,
  default: ReactSelect,
};

/**
 *
 * This component uses React Select: <https://react-select.com/home>
 *
 * Usage of this component has changed since we have upgraded to the latest version of `react-select`. For example, field values are now defined as separate objects. for example:
 *
 *       const options = [
 *       { value: 'chocolate', label: 'Chocolate' },
 *       { value: 'strawberry', label: 'Strawberry' },
 *       { value: 'vanilla', label: 'Vanilla' },
 *      { value: 'hazelnut', label: 'Hazelnut' },
 *      { value: 'rocky road', label: 'Rocky Road' }
 *     ]
 * For a full list of the changes, see <https://react-select.com/upgrade-guide>.
 *
 */
const selectLayout = (props) => {
  const layout = {};
  if (props.width != null) {
    layout.width = props.width;
  }
  if (props.minWidth != null) {
    layout.minWidth = props.minWidth;
  }
  if (props.maxWidth != null) {
    layout.maxWidth = props.maxWidth;
  }
  return layout;
};

const Select = forwardRef(({ selectType = "default", ...props }, ref) => {
  const theme = useTheme();
  const inputRef = useInputFocus(ref, props.focus);
  const { updateStyles = (s) => s } = props;

  const customStyles = updateStyles({
    menu: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      backgroundColor: themeGet("colors.surface")({ theme }),
      color: themeGet("colors.bodyText")({ theme }),
      fontSize: themeGet("fontSizes.1")({ theme }),
      border:
        themeGet("borderWidths.1")({ theme }) +
        " solid " +
        themeGet("colors.primary")({ theme }),
      borderRadius: themeGet("radii.2")({ theme }),
      marginBottom: "0",
      marginTop: themeGet("space.xs")({ theme }),
      overflow: "hidden",
      zIndex: 12,
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: "0",
      overflow: "auto",
      color: themeGet("colors.bodyText")({ theme }),
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: props.height
        ? props.height
        : themeGet("appScale.inputHeightDefault")({ theme }),
      boxShadow: "none",
      fontSize: themeGet("fontSizes.1")({ theme }),
      opacity: state.isDisabled ? 0.7 : 1,
      "&:hover": {
        borderColor: props.invalid
          ? themeGet("colors.danger")({ theme })
          : themeGet("colors.primary")({ theme }),
      },
      "&:focus-within": {
        outline: "0",
        borderColor: props.invalid
          ? themeGet("colors.danger")({ theme })
          : themeGet("colors.primary")({ theme }),
        boxShadow: props.invalid
          ? themeGet("shadows.thickOutline")({ theme }) +
            " " +
            themeGet("colors.danger30")({ theme })
          : themeGet("shadows.thickOutline")({ theme }) +
            " " +
            themeGet("colors.primary30")({ theme }),
      },
      borderColor: state.isFocused
        ? themeGet("colors.primary")({ theme })
        : controlBorderColor(theme, props),
      backgroundColor: themeGet("colors.surface")({ theme }),
      color: themeGet("colors.bodyText")({ theme }),
      borderRadius: themeGet("radii.2")({ theme }),
      overflow: "hidden",
      border: `1px solid ${controlBorderColor(theme, props)}`,
    }),
    singleValue: (provided) => ({
      ...provided,
      fontWeight: props.emphasis
        ? themeGet("fontWeights.2")({ theme })
        : provided.fontWeight,
    }),
    container: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      color: themeGet("colors.mutedText")({ theme }),
      fontSize: themeGet("fontSizes.1")({ theme }),
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: props.padding ? props.padding : "2px 4px",
      overflow: "visible",
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      padding: themeGet("space.xxs")({ theme }),
      color: state.isFocused
        ? themeGet("colors.primary")({ theme })
        : themeGet("colors.mutedText")({ theme }),
      "&:hover": {
        color: themeGet("colors.danger")({ theme }),
      },
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      padding: themeGet("space.xxs")({ theme }),
      color: state.isFocused
        ? themeGet("colors.primary")({ theme })
        : themeGet("colors.mutedText")({ theme }),
      "&:hover": {
        color: state.isFocused
          ? themeGet("colors.primaryDarkest")({ theme })
          : themeGet("colors.primary")({ theme }),
      },
    }),
    groupHeading: (provided) => ({
      ...provided,
      textTransform: "none",
      fontSize: themeGet("fontSizes.0")({ theme }),
      color: themeGet("colors.subText")({ theme }),
    }),
    multiValue: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      backgroundColor: "transparent",
      color: themeGet("colors.white")({ theme }),
      display: "flex",
      alignItems: "stretch",
      padding: "0 !important",
      overflow: "hidden",
      borderRadius: "15px",
      fontSize: themeGet("fontSizes.1")({ theme }),
      wordWrap: "break-word",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      backgroundColor: themeGet("colors.primary")({ theme }),
      color: themeGet("colors.white")({ theme }),
      display: "flex",
      alignItems: "center",
      alignSelf: "stretch",
      boxSizing: "border-box",
      padding: state.data.isFixed
        ? "4px 10px !important"
        : "4px 8px 4px 10px !important",
      fontSize: themeGet("fontSizes.0")({ theme }),
      fontWeight: themeGet("fontWeights.2")({ theme }),
      lineHeight: 1.2,
      wordWrap: "break-word",
      whiteSpace: "break-spaces",
      borderRadius: state.data.isFixed ? "15px" : "15px 0 0 15px",
    }),
    multiValueRemove: (provided, state) => {
      return {
        ...provided,
        backgroundColor: themeGet("colors.primary")({ theme }),
        color: themeGet("colors.white")({ theme }),
        borderLeft: `solid 1px ${themeGet("colors.primaryDark")({ theme })}`,
        padding: "4px 6px 4px 5px",
        minWidth: "24px",
        minHeight: 0,
        height: "auto",
        display: state.data.isFixed ? "none" : "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        alignSelf: "stretch",
        boxSizing: "border-box",
        borderRadius: "0 15px 15px 0",
        transition: themeGet("transition.transitionDefault")({ theme }),
        "&:hover": {
          backgroundColor: themeGet("colors.primaryDark")({ theme }),
          color: themeGet("colors.white")({ theme }),
        },
        svg: {
          display: "block",
        },
      };
    },
    option: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      minHeight: "24px",
      paddingTop: themeGet("space.s")({ theme }),
      paddingBottom: themeGet("space.s")({ theme }),
      paddingLeft: themeGet("space.s")({ theme }),
      paddingRight: themeGet("space.s")({ theme }),
      marginBottom: themeGet("space.xs")({ theme }),
      backgroundColor: state.isFocused
        ? themeGet("colors.primaryLightest")({ theme })
        : themeGet("colors.surface")({ theme }),
      color: themeGet("colors.bodyText")({ theme }),
      fontSize: themeGet("fontSizes.1")({ theme }),
      whiteSpace: "break-spaces",
    }),
    placeholder: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
      color: themeGet("colors.subText")({ theme }),
      fontSize: themeGet("fontSizes.1")({ theme }),
    }),
  });

  const components = { MultiValueRemove, ...(props?.components || {}) };

  const SelectComponent = SELECT_TYPES[selectType] ?? SELECT_TYPES.default;

  const component = (
    <Wrapper
      data-testid={props["data-testid"]}
      data-select-wrapper="true"
      {...selectLayout(props)}
    >
      {props.label && props.inputId && (
        <Label
          id={`${props.inputId}-label`}
          bold={props.bold}
          htmlFor={props.inputId}
          invalid={props.invalid}
        >
          {props.label}
          {props.mandatory && (
            <Small color="danger" ml="xs">
              *
            </Small>
          )}
        </Label>
      )}

      <SelectComponent
        {...props}
        emphasis={undefined}
        ref={inputRef}
        styles={customStyles}
        theme={theme}
        inputId={props.inputId}
        isMulti={props.isMulti}
        classNamePrefix={props.classNamePrefix}
        onChange={props.onChange}
        aria-label={props.label && props.inputId ? undefined : props.ariaLabel}
        aria-labelledby={
          props.label && props.inputId ? `${props.inputId}-label` : undefined
        }
        components={components}
      />
    </Wrapper>
  );

  return props.theme ? (
    <ThemeProvider theme={props.theme}>{component}</ThemeProvider>
  ) : (
    component
  );
});

Select.propTypes = {
  /** Points to options object, see example code above */
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /** Specifies the label for the `Select` */
  label: PropTypes.string,
  // ariaLabel prop must be specified if label is not provided
  ariaLabel: (props, propName) => {
    if (!props.label && (props[propName] == null || props[propName] === "")) {
      return new Error(
        `Missing prop \`${propName}\` not specified for Select component. When \`label\` is not provided, \`${propName}\` is required.`
      );
    }
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid propType \`${propName}\` supplied to Select component. Expected \`string\`, received \`${typeof props[
          propName
        ]}\`.`
      );
    }
    return null;
  },
  /** Makes label bold */
  bold: PropTypes.bool,
  /** Makes select disabled */
  isDisabled: PropTypes.bool,
  /** Specifies the ID for the rendered Select box. If you use the label prop label will automatically point to this ID, so this is required. */
  inputId: (props, propName) => {
    if (props.label && (props[propName] == null || props[propName] === "")) {
      return new Error(
        `Missing prop \`${propName}\` not specified for Select component. When \`label\` is provided, \`${propName}\` is required.`
      );
    }
    if (props[propName] && typeof props[propName] !== "string") {
      return new Error(
        `Invalid propType \`${propName}\` supplied to Select component. Expected \`string\`, received \`${typeof props[
          propName
        ]}\`.`
      );
    }
    return null;
  },
  /** Specifies the height of the select box control, make sure to include the unit, e.g. px */
  height: PropTypes.string,
  /** Specifies the padding of the value showed in the select box control, make sure to include the unit, e.g. px */
  padding: PropTypes.string,
  /** Specifies if the `Select` component is multi-select. */
  isMulti: PropTypes.bool,
  /** Specifies the `data-testid` attribute for testing. */
  dataTestId: PropTypes.string,
  /** Specifies prefix for the `#class` applied to the `Select` structures */
  classNamePrefix: PropTypes.string,
  /** Specifies `data-testid` for testing */
  "data-testid": PropTypes.string,
  /** Specifies `onChange` function for the input */
  onChange: PropTypes.func,
  /** Specifies the system design theme object */
  theme: PropTypes.object,
  /** Specify if you want react-select createable option */
  selectType: PropTypes.oneOf(["default", "creatable", "createable", "async"]),
  /** Specify if you want to overwrite existing customStyles */
  updateStyles: PropTypes.func,
  /** Applies invalid input styles */
  invalid: PropTypes.bool,
  /** Primary border and bold value for an active facet */
  emphasis: PropTypes.bool,
  /** Shows asterisk to denote a mandatory field */
  mandatory: PropTypes.bool,
  /** Focus on input */
  focus: PropTypes.bool,
  /** Allows overrides of react-select components */
  components: PropTypes.object,
};

/** @component */
export default Select;
